import {
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  readonly,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useOverviewPermissions } from '../permissions/useOverviewPermissions';
import type { OverviewViewProps, SensitiveViewMode } from '../types';
import { sensitiveValuesModeKey } from '../types';
import { createOverviewStore } from './createOverviewStore';

export type UseOverviewLogicProps = OverviewViewProps;

export const useOverviewLogic = ({
  projectId,
  user,
}: UseOverviewLogicProps) => {
  const route = useRoute();

  const store = createOverviewStore();
  const { mode } = useOverviewPermissions({ projectId, user });

  const mainRef = ref<HTMLElement | null>(null);
  const sideHeight = ref<number | null>(null);
  let resizeObserver: ResizeObserver | null = null;

  provide(sensitiveValuesModeKey, readonly(store.sensitiveMode));

  watch(
    () => route.params,
    () => store.dispatch({ type: 'RESET_SENSITIVE_MODE' }),
  );

  onMounted(() => {
    resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || !mainRef.value) return;
      const parentWidth = mainRef.value.parentElement?.clientWidth ?? 0;
      // When layout is stacked (main fills full width), no fixed height needed
      if (parentWidth > 0 && entry.contentRect.width > parentWidth * 0.8) {
        sideHeight.value = null;
      } else {
        sideHeight.value = entry.contentRect.height;
      }
    });
    if (mainRef.value) resizeObserver.observe(mainRef.value);
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });

  const state = reactive({
    sensitiveMode: store.sensitiveMode,
    mode,
    user,
    projectId,
    sideHeight,
  });

  const action = {
    setSensitiveMode(value: SensitiveViewMode) {
      store.dispatch({ type: 'SET_SENSITIVE_MODE', payload: value });
    },
  };

  return { state, action, mainRef };
};
